import React from "react";

import clsx from "clsx";

import {
  Box,
  ButtonProps,
  CircularProgress,
  Button as MuiButton,
  Tooltip,
} from "@material-ui/core";

import { trackEvent } from "@ip-utils";

export interface Props extends ButtonProps {
  icon?: React.ElementType;
  large?: boolean;
  loading?: boolean;
  outlined?: boolean;
  tooltip?: string;
}

export default function Button({
  children,
  color = "primary",
  disabled,
  icon: Icon,
  large,
  loading,
  outlined,
  tooltip,
  onClick,
  ...props
}: Props) {
  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    if (!disabled && !loading) {
      const label = Icon ? tooltip : children;
      if (label) {
        trackEvent("Button", `Click '${label}'`);
      }

      if (onClick) {
        onClick(e);
      }
    }
  }

  const buttonLoading = loading && <CircularProgress size="1em" />;

  if (Icon) {
    if (large) {
      children = (
        <Box
          alignItems="center"
          component="span"
          display="flex"
          flexDirection="column"
        >
          <Icon fontSize="large" />
          {children}
        </Box>
      );
    } else {
      children = buttonLoading || <Icon fontSize="inherit" />;
    }
  } else if (loading) {
    children = (
      <React.Fragment>
        {children && <span>{children}</span>}
        {buttonLoading}
      </React.Fragment>
    );
  }

  const className = clsx(color, {
    disabled: disabled || loading,
    icon: Icon && !large,
  });

  const variant = outlined ? "outlined" : "contained";

  const button = (
    <MuiButton
      className={className}
      variant={variant}
      onClick={handleClick}
      {...props}
    >
      {children}
    </MuiButton>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{button}</Tooltip>;
  }

  return button;
}
